import { useEffect, useState } from 'react';
import { Navigate, useLocation, useNavigate, useParams } from 'react-router-dom';
import { Lock, Eye, EyeOff, AlertCircle, CheckCircle2, Sparkles, ShieldAlert, ArrowRight, Mail, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { useAuth } from '@/contexts/AuthContext';
import { useBranding } from '@/contexts/BrandingContext';
import { toast } from 'sonner';
import { ROLES, DEFAULT_PATH_FOR_ROLE } from '@/lib/roles';

const PLATFORM_LOGO = '/branding/condutor-online-logo.png';
const PLATFORM_NAME = 'condutor.online';

/**
 * /first-login — ecrã obrigatório para utilizadores convidados (ou com password reset).
 * Só permite sair depois de escolher uma nova palavra-passe. Se `mustChangePassword` não
 * estiver ativo na sessão, redireciona para o landing default do role.
 */
export const FirstLoginPage = () => {
  const { user, changeMyPassword, logout, sendEmailVerification, verifyEmail, setVerifiedEmailPassword } = useAuth();
  const { branding } = useBranding();
  const navigate = useNavigate();
  const location = useLocation();
  const { id: verifyId, hash: verifyHash } = useParams();

  const [currentPwd, setCurrentPwd] = useState('');
  const [newPwd, setNewPwd] = useState('');
  const [confirmPwd, setConfirmPwd] = useState('');
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [loading, setLoading] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  const [verifyingEmail, setVerifyingEmail] = useState(false);
  const [verificationAttempted, setVerificationAttempted] = useState(false);
  const [verificationComplete, setVerificationComplete] = useState(false);
  const [passwordResetLink, setPasswordResetLink] = useState('');
  const [publicEmail, setPublicEmail] = useState('');
  const [error, setError] = useState('');
  const isEmailVerificationPage = location.pathname.startsWith('/email/verify');
  const isPublicPasswordSetup = isEmailVerificationPage && verificationComplete && verifyId && verifyHash;

  useEffect(() => {
    if (!verifyId || !verifyHash || verificationAttempted) return;

    const run = async () => {
      setError('');
      setVerificationAttempted(true);
      setVerifyingEmail(true);
      try {
        const r = await verifyEmail({
          id: verifyId,
          hash: verifyHash,
          queryString: location.search || '',
          skipRefresh: isEmailVerificationPage,
        });
        if (!r.ok) {
          setError(r.error);
          return;
        }
        setPasswordResetLink(r.passwordResetLink || '');
        setVerificationComplete(true);
        toast.success('Email validado com sucesso.');
      } finally {
        setVerifyingEmail(false);
      }
    };

    run();
  }, [isEmailVerificationPage, location.search, verificationAttempted, verifyEmail, verifyHash, verifyId]);

  // Gate: sem sessão → login; sem flag → landing default.
  if (!user && !isEmailVerificationPage) return <Navigate to="/login" replace />;
  if (user && !user.mustChangePassword && user.emailVerified && !isEmailVerificationPage) {
    return <Navigate to={DEFAULT_PATH_FOR_ROLE[user.role] || '/'} replace />;
  }

  const handleSendVerification = async () => {
    const email = user?.email || publicEmail.trim();
    setError('');
    if (!email) {
      setError('Indique o email da conta.');
      return false;
    }
    setResendLoading(true);
    const r = await sendEmailVerification({email});
    setResendLoading(false);
    if (!r.ok) {
      setError(r.error);
      return false;
    }
    if (!user) {
      toast.success(`Link de validação enviado para ${email}.`);
      return true;
    }
    toast.success(`Link de validação enviado para ${user.email}.`);
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if ((!isPublicPasswordSetup && !currentPwd) || !newPwd || !confirmPwd) {
      setError('Preencha todos os campos.');
      return;
    }
    if (newPwd !== confirmPwd) {
      setError('A nova palavra-passe e a confirmação não coincidem.');
      return;
    }
    if (newPwd.length < 8) {
      setError('A nova palavra-passe tem de ter pelo menos 8 caracteres.');
      return;
    }
    if (!isPublicPasswordSetup && newPwd === currentPwd) {
      setError('A nova palavra-passe tem de ser diferente da temporária.');
      return;
    }
    setLoading(true);
    if (isPublicPasswordSetup) {
      const r = await setVerifiedEmailPassword({
        id: verifyId,
        hash: verifyHash,
        queryString: location.search || '',
        passwordResetLink,
        password: newPwd,
      });
      setLoading(false);
      if (!r.ok) { setError(r.error); return; }
      toast.success('Palavra-passe definida. Pode iniciar sessão.');
      navigate('/login', { replace: true });
      return;
    }

    const r = await changeMyPassword({ currentPassword: currentPwd, newPassword: newPwd });
    setLoading(false);
    if (!r.ok) { setError(r.error); return; }
    if (!r.user?.emailVerified) {
      toast.success('Palavra-passe definida. Valide o email para ativar a conta.');
      await handleSendVerification();
      return;
    }
    toast.success('Palavra-passe definida. Bem-vindo!');
    navigate(DEFAULT_PATH_FOR_ROLE[r.user.role] || '/', { replace: true });
  };

  const handleLogout = () => {
    logout();
    navigate('/login', { replace: true });
  };

  return (
    <div className="min-h-screen w-full bg-slate-950 flex items-stretch" data-testid="first-login-page">
      {/* Left brand panel */}
      <div className="hidden lg:flex flex-1 relative overflow-hidden">
        <div className="absolute inset-0" style={{
          background: 'radial-gradient(circle at 25% 35%, rgba(234,179,8,0.35), transparent 55%), radial-gradient(circle at 80% 75%, rgba(37,99,235,0.18), transparent 55%), linear-gradient(135deg, #1a1200 0%, #0a0f1f 100%)'
        }} />
        <div className="absolute inset-0 opacity-30" style={{
          backgroundImage: 'linear-gradient(rgba(255,255,255,0.04) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.04) 1px, transparent 1px)',
          backgroundSize: '60px 60px',
        }} />
        <div className="absolute -top-20 -right-20 w-96 h-96 rounded-full opacity-20 blur-3xl" style={{ backgroundColor: '#facc15' }} />
        <div className="relative z-10 flex flex-col justify-between p-12 text-white w-full">
          <div className="flex items-center gap-5 flex-wrap">
            <div className="flex items-center gap-4">
              <div className="rounded-xl p-3" style={{ backgroundColor: '#facc15', color: '#0c1a3a' }}>
                <ShieldAlert size={32} />
              </div>
              <div>
                <h1 className="font-secondary font-bold text-2xl xl:text-3xl leading-tight">
                  Primeiro acesso
                </h1>
                <p className="text-sm text-slate-300">Defina a sua palavra-passe para continuar</p>
              </div>
            </div>
            <div className="flex items-center gap-2 ml-auto">
              <span className="text-[10px] uppercase tracking-wider text-slate-400 font-bold">Powered by</span>
              <div className="bg-black rounded-lg px-2.5 py-1.5 ring-1 ring-white/10">
                <img src={PLATFORM_LOGO} alt={PLATFORM_NAME} className="h-7 w-auto block" />
              </div>
            </div>
          </div>

          <div className="space-y-6 max-w-lg">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider" style={{ backgroundColor: '#facc15', color: '#0c1a3a' }}>
              <Sparkles size={12} />
              Segurança obrigatória
            </div>
            <h2 className="font-secondary font-bold text-4xl xl:text-5xl leading-tight">
              Escolha a sua <br />
              <span style={{ color: '#facc15' }}>palavra-passe pessoal</span>.
            </h2>
            <p className="text-slate-300 text-base leading-relaxed">
              Recebeu uma palavra-passe temporária por {branding.companyName || 'email'}.
              Por motivos de segurança, precisa de a substituir antes de aceder ao resto da plataforma.
            </p>
            <ul className="space-y-2 text-sm text-slate-300">
              <li className="flex items-center gap-2"><CheckCircle2 size={14} className="text-emerald-400" /> Mínimo 8 caracteres.</li>
              <li className="flex items-center gap-2"><CheckCircle2 size={14} className="text-emerald-400" /> Diferente da palavra-passe temporária.</li>
              <li className="flex items-center gap-2"><CheckCircle2 size={14} className="text-emerald-400" /> Só você a deve conhecer.</li>
            </ul>
          </div>

          <div className="flex items-center justify-between text-xs text-slate-500">
            <p>© {new Date().getFullYear()} {branding.companyName || PLATFORM_NAME}</p>
            <div className="flex items-center gap-1.5">
              <span>Powered by</span>
              <span className="font-bold text-slate-300">{PLATFORM_NAME}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Right form */}
      <div className="flex-1 flex items-center justify-center p-6 lg:p-12 bg-gradient-to-br from-amber-50 to-slate-50">
        <div className="w-full max-w-md">
          <Card className="p-8 shadow-2xl border-amber-100 relative overflow-hidden">
            <div className="absolute top-0 left-0 right-0 h-1.5" style={{ backgroundColor: '#facc15' }} />
            <div className="mb-6">
              <div className="flex items-center gap-2 mb-2">
                <Badge className="bg-amber-100 text-amber-800 border-amber-200 text-[10px] uppercase tracking-wider">
                  <ShieldAlert size={10} className="mr-1" /> Ação obrigatória
                </Badge>
              </div>
              <h2 className="text-2xl font-secondary font-bold text-slate-900">
                {(user?.mustChangePassword || isPublicPasswordSetup) ? 'Definir nova palavra-passe' : 'Validar email'}
              </h2>
              {user ? (
                <>
                  <p className="text-sm text-slate-500 mt-1">
                    Está a aceder como <strong>{user.name}</strong>
                    {user.role && <> · <span className="text-slate-700">{ROLES[user.role]?.label || user.role}</span></>}
                  </p>
                  <p className="text-xs text-slate-500 mt-0.5 font-mono">{user.email}</p>
                </>
              ) : (
                <p className="text-sm text-slate-500 mt-1">
                  Indique o email da conta para receber um novo link de validação.
                </p>
              )}
            </div>

            {verifyingEmail && (
              <div className="mb-5 flex items-start gap-2 p-3 rounded-md bg-blue-50 border border-blue-200 text-sm text-blue-800">
                <RefreshCw size={14} className="mt-0.5 flex-shrink-0 animate-spin" />
                <span>A validar o email...</span>
              </div>
            )}

            {(!user || !user.emailVerified) && (
              <div className="mb-5 rounded-lg border border-amber-200 bg-amber-50 p-4">
                <div className="flex items-start gap-3">
                  <div className="mt-0.5 rounded-md bg-amber-100 p-2 text-amber-700">
                    <Mail size={16} />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-semibold text-slate-900">
                      {verificationComplete ? 'Email validado' : 'Validação de email pendente'}
                    </p>
                    <p className="mt-1 text-xs leading-relaxed text-slate-600">
                      {verificationComplete
                        ? 'O email foi validado com sucesso. Pode voltar ao login e aceder à conta.'
                        : 'Envie o link de validação para ativar a conta. Depois de clicar no link recebido por email, esta página confirma a validação automaticamente.'}
                    </p>
                    {!user && !verificationComplete && (
                      <div className="mt-3 space-y-2">
                        <Label htmlFor="fl-public-email" className="text-xs">Email</Label>
                        <Input
                          id="fl-public-email"
                          type="email"
                          autoComplete="email"
                          value={publicEmail}
                          onChange={e => setPublicEmail(e.target.value)}
                          placeholder="email@empresa.pt"
                          data-testid="fl-public-email"
                        />
                      </div>
                    )}
                    {!verificationComplete && (
                      <Button
                        type="button"
                        onClick={handleSendVerification}
                        disabled={resendLoading || verifyingEmail}
                        className="mt-3 h-9 bg-slate-900 text-white hover:bg-slate-800"
                        data-testid="fl-send-verification"
                      >
                        {resendLoading ? (
                          <>
                            <RefreshCw size={14} className="mr-2 animate-spin" /> A enviar...
                          </>
                        ) : (
                          <>
                            <Mail size={14} className="mr-2" /> Enviar link de validação
                          </>
                        )}
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            )}

            {error && (
              <div className="mb-4 flex items-start gap-2 p-3 rounded-md bg-red-50 border border-red-200 text-sm text-red-700" data-testid="fl-error">
                <AlertCircle size={14} className="mt-0.5 flex-shrink-0" />
                <span>{error}</span>
              </div>
            )}

            {(user?.mustChangePassword || isPublicPasswordSetup) ? (
            <form onSubmit={handleSubmit} className="space-y-4" data-testid="first-login-form">
              {!isPublicPasswordSetup && (
              <div className="space-y-2">
                <Label htmlFor="fl-current">Palavra-passe temporária *</Label>
                <div className="relative">
                  <Lock size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
                  <Input id="fl-current" type={showCurrent ? 'text' : 'password'} autoComplete="current-password"
                         value={currentPwd} onChange={e => setCurrentPwd(e.target.value)}
                         className="pl-9 pr-10" data-testid="fl-current-pwd" />
                  <button type="button" onClick={() => setShowCurrent(s => !s)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700">
                    {showCurrent ? <EyeOff size={15} /> : <Eye size={15} />}
                  </button>
                </div>
              </div>
              )}

              <div className="space-y-2">
                <Label htmlFor="fl-new">Nova palavra-passe *</Label>
                <div className="relative">
                  <Lock size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
                  <Input id="fl-new" type={showNew ? 'text' : 'password'} autoComplete="new-password"
                         value={newPwd} onChange={e => setNewPwd(e.target.value)}
                         className="pl-9 pr-10" data-testid="fl-new-pwd" />
                  <button type="button" onClick={() => setShowNew(s => !s)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700">
                    {showNew ? <EyeOff size={15} /> : <Eye size={15} />}
                  </button>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="fl-confirm">Confirmar nova palavra-passe *</Label>
                <div className="relative">
                  <Lock size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
                  <Input id="fl-confirm" type={showNew ? 'text' : 'password'} autoComplete="new-password"
                         value={confirmPwd} onChange={e => setConfirmPwd(e.target.value)}
                         className="pl-9 pr-10" data-testid="fl-confirm-pwd" />
                </div>
              </div>

              <Button type="submit" className="w-full bg-amber-500 hover:bg-amber-600 text-slate-900 font-bold h-11" disabled={loading} data-testid="fl-submit">
                {loading
                  ? 'A guardar...'
                  : <>Definir palavra-passe {isPublicPasswordSetup ? '' : (user?.emailVerified ? 'e entrar' : 'e validar email')} <ArrowRight size={16} className="ml-2" /></>}
              </Button>
            </form>
            ) : (
              <div className="rounded-md bg-slate-50 p-4 text-sm text-slate-600">
                A conta fica ativa assim que o email for validado. Se já clicou no link, atualize a página ou volte a abrir
                o link recebido.
              </div>
            )}

            <div className="mt-5 pt-4 border-t border-slate-100 text-center">
              {user ? (
                <button type="button" onClick={handleLogout} className="text-xs text-slate-500 hover:text-slate-800 hover:underline" data-testid="fl-logout">
                  Não sou eu · terminar sessão
                </button>
              ) : (
                <button type="button" onClick={() => navigate('/login')} className="text-xs text-slate-500 hover:text-slate-800 hover:underline" data-testid="fl-login">
                  Voltar ao login
                </button>
              )}
            </div>
          </Card>

          <p className="text-center text-xs text-slate-500 mt-6">
            Dúvidas? Contacte o administrador da sua organização.
          </p>
        </div>
      </div>
    </div>
  );
};
